import { AsyncPipe, NgTemplateOutlet } from '@angular/common'
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  inject,
} from '@angular/core'
import { FormsModule } from '@angular/forms'
import { MatIconButton } from '@angular/material/button'
import { MatError, MatFormField, MatHint, MatLabel } from '@angular/material/form-field'
import { MatIcon } from '@angular/material/icon'
import { MatInput } from '@angular/material/input'
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu'
import { MatTab, MatTabGroup } from '@angular/material/tabs'
import { MatToolbar } from '@angular/material/toolbar'
import { ActivatedRoute } from '@angular/router'
import {
  ElderHeaderComponent,
  ElderPaneContentComponent,
  ElderPaneHeaderComponent,
  ElderPaneTitleComponent,
  ElderSelectComponent,
  ElderSelectValueDirective,
  ElderSinglePaneWrapperComponent,
  ElderTabGroupRoutingDirective,
  ElderThemeDirective,
  ElderToastService,
  ElderValidationErrorDirective,
} from '@elderbyte/ngx-starter'
import { LoggerFactory } from '@elderbyte/ts-logger'
import { Instant } from '@js-joda/core'
import { ElderTranslatePipe } from '@elderbyte/ngx-starter'
import { BehaviorSubject, Observable } from 'rxjs'
import { IdValidator } from '../../../../features/validators/id-validator'
import { FacilityContextService } from '../../../context/facility-context/facility-context.service'
import { FacilitySelectDirective } from '../../../facilities/facility-select.directive'
import { PrinterSelectDirective } from '../../../output/printer/printer-select.directive'
import {
  ControlTerminalDialogService,
  StorageTerminalCtx,
  TerminalCtx,
  WorkStationTerminalCtx,
} from '../../../terminals/dialog/control-terminal-dialog.service'
import { WorkStationTableComponent } from '../../workstations/work-station-table/work-station-table.component'
import { ControlCenter, ControlCenterType, PrinterConfiguration } from '../control-center'
import { ControlCenterTypeSelectDirective } from '../control-center-type-select/control-center-type-select.directive'
import { ControlCenterService } from '../control-center.service'

@Component({
  selector: 'app-control-center-detail',
  templateUrl: './control-center-detail.component.html',
  styleUrls: ['./control-center-detail.component.scss'],
  imports: [
    ElderSinglePaneWrapperComponent,
    ElderPaneHeaderComponent,
    ElderPaneTitleComponent,
    ElderPaneContentComponent,
    MatToolbar,
    ElderThemeDirective,
    MatIconButton,
    MatIcon,
    NgTemplateOutlet,
    FormsModule,
    MatTabGroup,
    ElderTabGroupRoutingDirective,
    MatTab,
    MatFormField,
    MatLabel,
    ElderSelectComponent,
    FacilitySelectDirective,
    MatInput,
    MatError,
    ElderValidationErrorDirective,
    ControlCenterTypeSelectDirective,
    MatHint,
    PrinterSelectDirective,
    ElderSelectValueDirective,
    WorkStationTableComponent,
    ElderHeaderComponent,
    MatMenuTrigger,
    MatMenu,
    MatMenuItem,
    AsyncPipe,
    ElderTranslatePipe,
  ],
})
export class ControlCenterDetailComponent implements OnInit {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  private readonly log = LoggerFactory.getLogger(this.constructor.name)
  private readonly _controlCenter$: BehaviorSubject<ControlCenter> = new BehaviorSubject(null)
  private _creationMode: boolean
  public ControlCenterType = ControlCenterType
  @Input()
  public embedded = false
  @Output()
  public close: EventEmitter<Event> = new EventEmitter<Event>()
  public readonly idValidatorPattern = IdValidator.pattern
  private controlCenterService = inject(ControlCenterService)
  private appContextService = inject(FacilityContextService)
  private toastService = inject(ElderToastService)
  private terminalDialogService = inject(ControlTerminalDialogService)

  /***************************************************************************
   *                                                                         *
   * Constructor                                                             *
   *                                                                         *
   **************************************************************************/

  constructor() {
    const route = inject(ActivatedRoute)
    this.controlCenter = route.snapshot.data['controlCenter']
  }

  /***************************************************************************
   *                                                                         *
   * Life Cycle                                                              *
   *                                                                         *
   **************************************************************************/

  public ngOnInit(): void {
    if (!this.controlCenter) {
      this.controlCenter = this.initNewControlCenter()
    }
  }

  /***************************************************************************
   *                                                                         *
   * Getter / Setters                                                        *
   *                                                                         *
   **************************************************************************/

  public get controlCenter$(): Observable<ControlCenter> {
    return this._controlCenter$.asObservable()
  }

  public get controlCenter(): ControlCenter {
    return this._controlCenter$.getValue()
  }

  @Input()
  public set controlCenter(controlCenter: ControlCenter) {
    if (controlCenter) {
      if (!controlCenter.labelPrinter) {
        controlCenter.labelPrinter = new PrinterConfiguration()
      }

      if (!controlCenter.documentPrinter) {
        controlCenter.documentPrinter = new PrinterConfiguration()
      }

      this._creationMode = !controlCenter.id
      this._controlCenter$.next(controlCenter)
    }
  }

  public get creationMode(): boolean {
    return this._creationMode
  }

  public get deletionEnabled(): boolean {
    return !this.creationMode
  }

  /***************************************************************************
   *                                                                         *
   * Public API                                                              *
   *                                                                         *
   **************************************************************************/

  public openTerminalDialog(event: Event): void {
    const cc = this.controlCenter
    if (cc) {
      const ctx = this.buildTerminalCtx(cc)
      this.log.debug('Opening terminal dialog with ctx: ', ctx)
      this.terminalDialogService.openTerminalDialog(ctx)
    }
  }

  public onSave(event: Event): void {
    this.save()
  }

  public onDelete(event: Event): void {
    this.delete()
  }

  public onClose(event: Event): void {
    this.emitClose(event)
  }

  /***************************************************************************
   *                                                                         *
   * Private Methods                                                         *
   *                                                                         *
   **************************************************************************/

  private save(): void {
    this.controlCenterService.save(this.controlCenter).subscribe({
      next: (controlCenter) => {
        this.controlCenter = controlCenter
        this.toastService.info('messages.persisting_successful')
      },
      error: (err) => this.toastService.error('messages.saving_failed', err),
    })
  }

  private delete(): void {
    this.controlCenterService.delete(this.controlCenter).subscribe({
      next: () => this.emitClose(),
      error: (err) => this.toastService.error('messages.deleting_failed', err),
    })
  }

  private emitClose(event?: Event): void {
    this.close.emit(event)
  }

  private initNewControlCenter(): ControlCenter {
    const controlCenter = new ControlCenter()
    const currentFacility = this.appContextService.facility
    if (currentFacility) {
      controlCenter.facility = this.appContextService.facility.id
    }
    return controlCenter
  }

  private buildTerminalCtx(cc: ControlCenter): TerminalCtx {
    switch (cc.type) {
      case ControlCenterType.WORK_STATION:
        return TerminalCtx.workStation(
          cc.id,
          WorkStationTerminalCtx.live(Instant.now(), null, null, true)
        )
      case ControlCenterType.STORAGE:
        return TerminalCtx.storage(cc.id, new StorageTerminalCtx())
    }
  }
}
